/**
 * 功能选择页 — 网格布局
 * 只显示系统启用且当前用户有权限的功能模块；仅一个可用模块时自动直接进入
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, GraduationCap, Share2, ClipboardList, LogOut, Loader2, Layers } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { getFunctionEntryRoute } from '@/utils/functionEntry';
import styles from './FunctionSelector.module.css';

const ALL_FUNCTIONS = [
  { key: 'exam', label: '学习考试', icon: GraduationCap, desc: '任务管理、试卷管理、在线考试' },
  { key: 'knowledge', label: '知识传承', icon: BookOpen, desc: '知识档案、AI解析、问答搜索' },
  { key: 'share', label: '分享平台', icon: Share2, desc: '内容推介、客户管理、浏览分享' },
  { key: 'work', label: '日常工作', icon: ClipboardList, desc: '工作记录、拜访汇报' },
  { key: 'drawing', label: '图纸管理', icon: Layers, desc: '素材图纸、产品组合、参数卡与说明书' },
];

export default function FunctionSelector() {
  const navigate = useNavigate();
  const { user, functions, switchFunction, logout } = useAuth();
  // null = 系统启用列表获取失败（回退为仅按用户权限显示，避免接口异常把用户锁在外面）
  const [enabledSystems, setEnabledSystems] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.request<{ code: number; data?: { enabled_systems: string[] } }>('/system-config/enabled').then(res => {
      setEnabledSystems(res.data?.enabled_systems || []);
    }).catch(() => setEnabledSystems(null)).finally(() => setLoading(false));
  }, []);

  // 按 function 分组
  const funcMap: Record<string, { role_id: number; role_name: string }[]> = {};
  functions.forEach(f => {
    if (!funcMap[f.function]) funcMap[f.function] = [];
    funcMap[f.function].push({ role_id: f.role_id, role_name: f.role_name });
  });

  // 只显示系统已启用且当前用户有权限（被授予角色）的模块
  const visibleFunctions = ALL_FUNCTIONS.filter(
    fn => (enabledSystems === null || enabledSystems.includes(fn.key)) && (funcMap[fn.key]?.length ?? 0) > 0
  );

  const handleSelect = async (funcKey: string) => {
    const roles = funcMap[funcKey];
    if (!roles || roles.length === 0) return;
    try {
      await switchFunction(funcKey, roles[0].role_id);
      navigate(getFunctionEntryRoute(funcKey, roles[0]?.role_name || ''));
    } catch {}
  };

  // 仅一个可用模块时自动直接进入（登录单模块直进的兜底，覆盖"授权多个但系统只启用一个"等情况）
  const autoTried = useRef(false);
  useEffect(() => {
    if (loading || autoTried.current) return;
    if (visibleFunctions.length === 1) {
      autoTried.current = true;
      handleSelect(visibleFunctions[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, enabledSystems, functions]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.brand}>
          <BookOpen size={28} />
          <div><h1>大满书院</h1><span>知识传承系统</span></div>
        </div>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{user?.user_name || user?.account}</span>
          <button className={styles.logoutBtn} onClick={logout}><LogOut size={16} /> 退出登录</button>
        </div>
      </div>
      <div className={styles.body}>
        <h2 className={styles.title}>{user?.user_name || '用户'}，请选择要进入的功能模块</h2>
        {loading ? (
          <div style={{padding:40}}><Loader2 size={24} className="spin"/></div>
        ) : visibleFunctions.length === 0 ? (
          <div style={{textAlign:'center',color:'#9ca3af',padding:40}}>暂无可用的功能模块</div>
        ) : (
          <div className={styles.grid} style={visibleFunctions.length <= 2 ? {gridTemplateColumns: `repeat(${visibleFunctions.length}, 1fr)`} : {}}>
            {visibleFunctions.map((fn, i) => {
              const roles = funcMap[fn.key];
              if (!roles || roles.length === 0) return null;
              return (
                <motion.div key={fn.key}
                  className={`${styles.card} ${styles.active}`}
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => handleSelect(fn.key)}>
                  <fn.icon size={40} strokeWidth={1.5} />
                  <h3>{fn.label}</h3>
                  <p className={styles.desc}>{fn.desc}</p>
                  <span className={`${styles.badge} ${styles.badgeActive}`}>
                    角色: {roles[0].role_name}
                  </span>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
